/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/layout/AppLayout.tsx
 *
 * Rôle :
 *   Layout principal qui wrappe toutes les routes (sauf splash et certaines
 *   pages plein écran type Scanner). Inclut le Header permanent (avec les
 *   2 logos officiels §8.4), un <main id="main-content"> pour la cible du
 *   skip link, et le Footer permanent (§13.5.4 bandeau partenaires +
 *   §9.3 crédits auteur).
 *
 * Réutilisable :
 *   100 %. Tous les futurs apps DRANE peuvent l'adopter tel quel — il
 *   suffit de remplacer Header / Footer si on change la marque.
 *
 * @author Mika
 * @since 2026-05-04
 */

import type { ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { BottomNav } from './BottomNav';
import { EventThemeApplier } from './EventThemeApplier';
import { OfflineBanner, InstallPrompt } from '@/components/ui';
// FIX 2026-05-11 — tracking parcours visiteur automatique (demande Mika
// "tracking extrêmement performant"). Dispatch session_start au mount,
// page_view + route_change à chaque navigation, drain batch toutes les
// 30s vers /api/events.php. Désactivé pour mineurs côté hook + serveur.
import { useUserTracking } from '@/hooks/useUserTracking';
// FIX 2026-05-20 (compte plastron de démo) : modale pédagogique
// globale (cf. CLAUDE.md §5/§24 2026-05-20). Montée UNE SEULE FOIS
// ici → accessible depuis n'importe quel handler via le store
// `usePlastronGuardStore` ou le hook `usePlastronGuard()`.
import { PlastronGuardModal } from '@/components/system/PlastronGuardModal';
// FIX 2026-05-20 #3 (Mika — message push plein écran au login démo) :
// modale d'accueil affichée UNE FOIS par session quand un plastron
// se connecte. Recommande d'utiliser un vrai compte.
import { PlastronWelcomeModal } from '@/components/system/PlastronWelcomeModal';
// FIX 2026-05-25 (sprint borne) : watcher d'inactivité pour le mode
// kiosque (?kiosk=1 + plastron). Auto-redirect vers /mon-espace après
// N minutes sans interaction. Voir CLAUDE.md §5 entrée 2026-05-25.
import { KioskInactivityWatcher } from '@/components/system/KioskInactivityWatcher';
// FIX 2026-05-26 (demande Mika J-1) — Pull-to-refresh natif sur PWA
// installée (Safari iOS standalone + Chrome Android standalone n'ont
// pas le natif). Tirer vers le bas depuis le haut → reload de l'app.
// Cf. CLAUDE.md §5 entrée 2026-05-26.
import { PullToRefreshOverlay } from '@/components/system/PullToRefreshOverlay';
import styles from './AppLayout.module.css';

export interface AppLayoutProps {
  /** Slot d'actions du header (notifications, profil…). */
  headerActions?: ReactNode;
  /** Si true, masque le footer complet (utile pour Scanner plein écran). */
  hideFooter?: boolean;
  /**
   * Si true, masque la bottom nav (utile pour Splash, Onboarding,
   * Scanner plein écran, écrans d'inscription/login linéaires).
   * Par défaut false — c'est la règle Mika 2026-05-11 : la nav doit
   * être permanente pour ne pas se perdre.
   */
  hideBottomNav?: boolean;
  /** Contenu — soit children, soit Outlet du router (cas par défaut). */
  children?: ReactNode;
}

/**
 * Routes où la BottomNav est MASQUÉE automatiquement (pattern Stitch +
 * exigence Mika). Les pages admin gèrent leur propre nav (sidebar).
 * Cf. CLAUDE.md §5 entrée 2026-05-11 (refonte nav).
 */
const HIDE_NAV_PATTERNS: readonly RegExp[] = [
    /^\/splash$/,
    /^\/onboarding$/,
    /^\/role$/,
    /^\/inscription(\/|$)/,
    /^\/install-help-ios$/,
    /^\/admin($|\/)/,
    /^\/scanner$/,
    /^\/exposant\/scanner$/,
];

function shouldHideBottomNav(pathname: string): boolean {
    return HIDE_NAV_PATTERNS.some((re) => re.test(pathname));
}

export function AppLayout({
    headerActions,
    hideFooter = false,
    hideBottomNav,
    children,
}: AppLayoutProps) {
    const location = useLocation();
    const navHidden = hideBottomNav ?? shouldHideBottomNav(location.pathname);

    // Tracking automatique — démarre le drainer périodique au premier mount
    // et dispatch page_view/route_change à chaque changement de route.
    useUserTracking();

    return (
        <div
            className={styles.shell}
            data-with-bottom-nav={navHidden ? 'false' : 'true'}
        >
            <Header actions={headerActions} />

            {/* Pull-to-refresh overlay (FIX 2026-05-26) — monté UNE FOIS,
                actif partout sauf scanner/admin (la nav y est masquée
                mais le hook lui-même se base sur scrollY===0 + 1 doigt,
                donc compatible). Affiche flèche/spinner uniquement
                quand l'utilisateur tire. */}
            <PullToRefreshOverlay />

            {/* Bannière hors-ligne / sync — visible quand pertinent (§18). */}
            <OfflineBanner />

            {/* FIX 2026-05-18 (demande Mika) — invite d'installation PWA :
                Android/desktop (beforeinstallprompt) ou iOS (marche à
                suivre). Se masque seule si déjà installée / refusée. */}
            <InstallPrompt />

            <main
                id="main-content"
                className={`${styles.main} ${navHidden ? '' : styles.mainWithNav}`}
                tabIndex={-1}
            >
                {children ?? <Outlet />}
            </main>

            {!hideFooter && <Footer />}

            {/* Bottom nav permanente (sauf routes spéciales — cf.
                HIDE_NAV_PATTERNS). Pattern JANE 2019, exigence Mika
                2026-05-11. */}
            <BottomNav hidden={navHidden} />

            {/* Théming multi-événement à chaud (no-op si pas de
                branding serveur → thème TNE compilé conservé). */}
            <EventThemeApplier />

            {/* Toasts : le ToastViewport est désormais monté au niveau
                App (FIX 2026-07-05 LOT 4) — une seule instance globale
                qui couvre AUSSI les routes plein écran hors layout
                (/exposant/ma-fiche, /controle, …). Plus rien à monter ici. */}

            {/* FIX 2026-05-20 (compte plastron de démo) — modale
                pédagogique globale, montée UNE SEULE FOIS. Pilotée
                par le store usePlastronGuardStore (ou le hook
                usePlastronGuard côté handlers). */}
            <PlastronGuardModal />

            {/* FIX 2026-05-20 #3 (Mika — message push plein écran au
                login démo) — modale d'accueil affichée UNE FOIS par
                session quand un plastron se connecte. Recommande
                d'utiliser un vrai compte. */}
            <PlastronWelcomeModal />

            {/* FIX 2026-05-25 (sprint borne) — watcher d'inactivité
                actif UNIQUEMENT si mode kiosque (`?kiosk=1` + role=plastron).
                Auto-redirect vers /mon-espace après N min (default 3) sans
                interaction. Coût runtime nul hors mode kiosque (composant
                ne pose aucun listener). */}
            <KioskInactivityWatcher />
        </div>
    );
}
